/**
 * Iter-339 — Admin Production Health Dashboard.
 * URL: /admin/health
 *
 * One aggregator screen for all operational signals. Each card links
 * back to the deeper diagnostic page when relevant.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, CheckCircle2, AlertTriangle, RefreshCw, Database, LinkIcon,
  Mail, MessageSquare, Bell, CreditCard, CalendarClock, Cog, ShieldAlert, Trash2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import CommHealthTile from './CommHealthTile';
import DeploymentSelfTestCard from '../../components/admin/DeploymentSelfTestCard';  // Iter-411v

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const SECTIONS = [
  { key: 'test_data',        title: 'Test Data Audit',        icon: Database,      link: '/admin/marketplace/vendors' },
  { key: 'orphaned_records', title: 'Orphaned Records',       icon: LinkIcon },
  { key: 'failed_comms',     title: 'Failed Communications',  icon: Mail },
  { key: 'stripe',           title: 'Payments & Payouts',     icon: CreditCard,   link: '/admin/staff-payments' },
  { key: 'calendar',         title: 'Calendar Syncs',         icon: CalendarClock },
  { key: 'background_jobs',  title: 'Background Jobs',        icon: Cog },
  { key: 'auth_errors',      title: 'Authentication',         icon: ShieldAlert },
];

export default function AdminHealthDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/health/production`, AUTH());
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load health report');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const purgeContracts = async () => {
    if (!window.confirm('Deactivate all test-named contracts across production?')) return;
    setPurging(true);
    try {
      const r = await axios.post(`${API}/admin/audit/purge-test-contracts`, {}, AUTH());
      toast.success(`Purged ${r.data.purged} contracts`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge failed');
    } finally { setPurging(false); }
  };

  if (loading && !data) {
    return <div className="min-h-[80vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>;
  }
  if (!data) return null;

  const overallClean = data.overall_clean;

  return (
    <div className="min-h-[100dvh] bg-slate-50">
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Ops · Admin</div>
            <h1 className="text-xl font-bold" data-testid="health-dashboard-title">Production Health Dashboard</h1>
            <div className="text-xs text-slate-500 mt-1">
              Environment: <span className={`font-medium ${data.environment === 'production' ? 'text-emerald-700' : 'text-amber-700'}`}>{data.environment}</span> ·
              Generated: {new Date(data.generated_at).toLocaleString()}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full ${overallClean ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} data-testid="overall-status">
              {overallClean ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
              {overallClean ? 'ALL SYSTEMS CLEAN' : 'ATTENTION NEEDED'}
            </div>
            <Button size="sm" onClick={load} variant="outline" data-testid="refresh-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Iter-411v — one-click post-deploy contract check. */}
        <DeploymentSelfTestCard />
        <CommHealthTile />
        <Link
          to="/admin/discrepancies"
          className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 hover:shadow-sm transition"
          data-testid="discrepancies-tile-link"
        >
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Ops · Data integrity</div>
            <div className="font-semibold text-slate-900">Discrepancy Report</div>
            <div className="text-xs text-slate-600 mt-0.5">
              Calendar, applicants, staffing, briefings, invoices, confirmations —
              every "count says N, records say M" mismatch in one clickable list.
            </div>
          </div>
          <span className="text-sm text-cyan-700 font-medium">Open →</span>
        </Link>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {SECTIONS.map(s => (
          <HealthCard key={s.key} title={s.title} Icon={s.icon} link={s.link} data={data.sections[s.key]}
            testId={`card-${s.key}`}
            extra={s.key === 'test_data' && (data.sections.test_data?.total || 0) > 0 && (
              <Button size="sm" onClick={purgeContracts} disabled={purging} className="bg-red-600 hover:bg-red-700 mt-2 text-xs w-full" data-testid="purge-contracts-btn">
                {purging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Trash2 className="w-3 h-3 mr-1" /> Purge test contracts</>}
              </Button>
            )}
          />
        ))}
        </div>
      </main>
    </div>
  );
}

function HealthCard({ title, Icon, data, link, extra, testId }) {
  if (!data) return null;
  const clean = data.clean;
  const total = data.total || 0;
  const breakdown = data.by_table || data.by_check || null;
  return (
    <Card className="p-4" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={`w-4 h-4 ${clean ? 'text-emerald-600' : 'text-amber-600'}`} />
          <div className="font-semibold truncate">{title}</div>
        </div>
        <div className={`text-xs px-2 py-0.5 rounded-full ${clean ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
          {clean ? 'OK' : `${total} issue${total !== 1 ? 's' : ''}`}
        </div>
      </div>

      {!clean && (
        <div className="mt-3 space-y-1 text-sm">
          {breakdown ? (
            Object.entries(breakdown).map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-slate-600 truncate">{k}</span>
                <span className="font-medium">{v}</span>
              </div>
            ))
          ) : (
            Object.entries(data).filter(([k]) => !['total','clean','window_hours','by_table','by_check'].includes(k)).map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-slate-600">{k.replace(/_/g, ' ')}</span>
                <span className="font-medium">{v}</span>
              </div>
            ))
          )}
        </div>
      )}

      {extra}

      {link && (
        <Link to={link} className="mt-3 inline-block text-xs text-teal-700 hover:underline">
          Open detailed view →
        </Link>
      )}
    </Card>
  );
}
